import { RateSource } from '@/modules/common/types/rate-source';
import { ProjectTeamMember } from '@/modules/projects/types/project-team-member';

export interface RateCalculationContext {
  currentRateSource: RateSource;
  currentValues: { flatRate?: number };
  projectData?: { teamMembers?: ProjectTeamMember[] };
  defaultRateSource?: RateSource;
}

/**
 * Get the effective rate source for a team member based on the project's rate source
 */
export function getEffectiveRateSource(
  member: ProjectTeamMember,
  context: RateCalculationContext
): RateSource {
  const { currentRateSource, projectData, defaultRateSource } = context;

  if (currentRateSource !== RateSource.ProjectRateCard) {
    // When project rate source is NOT ProjectRateCard, all members use the project rate source
    return currentRateSource;
  } else {
    // When project rate source IS ProjectRateCard, each member uses their individual activeRateSource
    // Use the current member's activeRateSource (which may have been updated by user selection)
    // If it's not set, fall back to original data
    if (member.activeRateSource) {
      return member.activeRateSource;
    }

    // Fallback to original data only if member doesn't have activeRateSource set
    const sourceMember = projectData?.teamMembers?.find(tm => tm.id === member.id);

    return sourceMember?.activeRateSource || defaultRateSource || RateSource.UserExternalRate;
  }
}

/**
 * Calculate the rate value for a team member based on their effective rate source
 */
export function getMemberRate(member: ProjectTeamMember, context: RateCalculationContext): number {
  const effectiveRateSource = getEffectiveRateSource(member, context);
  const { currentValues, projectData } = context;

  // Get the member data with all rate values (from project or mock data)
  const originalMember = projectData?.teamMembers?.find(tm => tm.id === member.id);
  const sourceMember = originalMember || member;

  switch (effectiveRateSource) {
    case RateSource.UserExternalRate:
      return sourceMember.userExternalRateValue || 0;
    case RateSource.SeniorityBasedRate:
      return sourceMember.seniorityBasedRateValue || 0;
    case RateSource.RoleBasedRate:
      return sourceMember.roleBasedRateValue || 0;
    case RateSource.GlobalDefaultRate:
      return sourceMember.globalDefaultRateValue || 0;
    case RateSource.Custom:
      return sourceMember.customRateValue || 0;
    case RateSource.FlatRate:
      return currentValues.flatRate || 0;
    default:
      return 0;
  }
}
